import type { ReactNode } from "react";
import { type FieldValues, type FieldPath, Controller } from "react-hook-form";
import {
  FormControlWrapper,
  type FormControlWrapperProps,
  FormErrorText,
  FormInputContainer,
} from "@apac/form-ui";
import { type CoreIconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import { RequiredBadge } from "@hsbc/portal";
import { Icon } from "@tenant/ui";
import styles from "./styles.module.scss";

type FormInputWrapperProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  label?: ReactNode;
  labelIconName?: CoreIconNameEnum;
  onLabelClick?: () => void;
  children?: ReactNode;
} & FormControlWrapperProps<TFieldValues, TName>;

export const FormInputWrapper = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  label,
  labelIconName,
  onLabelClick,
  children,
  ...props
}: FormInputWrapperProps<TFieldValues, TName>): JSX.Element => {
  const [block, cn] = useModuleBEM(styles, "FormInputWrapper");
  const { t } = useI18nContext();

  const { required, ...restProps } = props;

  return (
    <div className={block}>
      {Boolean(label) && (
        <span className={cn("label-row")}>
          <span className={cn("label")} onClick={() => onLabelClick?.()}>
            {label}
            {labelIconName != null ? <Icon iconName={labelIconName} /> : null}
          </span>
          {required === true ? (
            <RequiredBadge label={t("apac.form.requiredLabel")} />
          ) : null}
        </span>
      )}
      {"render" in restProps ? (
        <Controller
          {...restProps}
          render={(context) => {
            const error = context.fieldState.error?.message;

            return (
              <>
                <FormInputContainer className={cn("input-container")}>
                  {restProps.render(context)}
                </FormInputContainer>

                {Boolean(error) && <FormErrorText error={error} />}
              </>
            );
          }}
        />
      ) : (
        <FormControlWrapper {...restProps}>
          <FormInputContainer>{children}</FormInputContainer>
        </FormControlWrapper>
      )}
    </div>
  );
};
